import {Component, computed, effect, inject, model, untracked} from "@angular/core";
import {FormsModule} from "@angular/forms";
import {InputText} from "primeng/inputtext";
import {FloatLabel} from "primeng/floatlabel";
import {Divider} from "primeng/divider";
import {InputNumber} from "primeng/inputnumber";
import {Select} from "primeng/select";
import {InputGroup} from "primeng/inputgroup";
import {Button} from "primeng/button";
import {Listbox} from "primeng/listbox";
import {Duration} from "luxon";
import {
    DatasetStore,
    DatasetTypes,
    DatasetTypesDescr,
    ElementTypes,
    WidgetElementMetadata
} from "@entities/widget-element";
import {WidgetConstructorStore} from "@widgets/widget-constructor";
import {Metric} from "../../model/constructor-menu-nodes";

@Component({
    selector: "constructor-settings",
    imports: [
        InputText,
        FloatLabel,
        Divider,
        InputNumber,
        FormsModule,
        Select,
        InputGroup,
        Button,
        Listbox
    ],
    templateUrl: "./settings.component.html",
    styleUrl: "./settings.component.scss"
})
export class SettingsComponent {
    private constructorStore = inject(WidgetConstructorStore);
    private datasetStore = inject(DatasetStore);

    readonly element = computed(() => this.constructorStore.editingElement());
    readonly datasets = computed(() => this.element()?.data.content.datasets || []);
    readonly temporalCount = computed(() => this.element()?.data.metadata.temporalCount);
    readonly precision = computed(() => this.element()?.data.metadata.precision);
    readonly datasetType = computed(() => {
        const type = this.element()?.data.metadata.datasetType;
        if (type) return {
            name: this.datasetsDescr[type],
            value: type
        }; else return undefined;
    });
    readonly threshold = computed(() => this.element()?.data.metadata.threshold);
    readonly settings = computed(() => this.constructorStore.datasetSettings()?.data());
    readonly settingsLoading = computed(() => !!this.constructorStore.datasetSettings()?.isLoading());

    readonly datasetsToRemove = model<Metric[]>([]);
    readonly temporalCountModel = model(
        Duration.fromMillis(Math.max(this.temporalCount() || 0, 1)).shiftTo("days", "hours")
    );
    readonly precisionModel = model(this.precision());
    readonly thresholdMinModel = model(this.threshold()?.min);
    readonly thresholdMaxModel = model(this.threshold()?.max);

    readonly name = computed(() => this.widget().name);
    readonly widget = computed(() => this.constructorStore.editingWidget()!);

    readonly settingsMin = computed(() => this.settings()?.min ? Math.floor(this.settings()!.min!) : null);
    readonly settingsMax = computed(() => this.settings()?.max ? Math.floor(this.settings()!.max!) : null);
    readonly precisionMin = 5;
    readonly precisionMax = 100;
    readonly maxDays = 29;
    readonly maxHours = 23;

    readonly datasetTypes = DatasetTypes;
    readonly datasetsDescr = DatasetTypesDescr;
    readonly datasetTypesOptions = Object.values(this.datasetTypes).map(type => ({
        name: this.datasetsDescr[type],
        value: type
    }));

    resetInputsToInit = effect(() => {
        if (this.element()) {
            untracked(() => {
                const rawCount = this.temporalCount();
                const safeCount = rawCount && rawCount > 0 ? rawCount : 24 * 60 * 60 * 1000; // 1 день в миллисекундах
                this.temporalCountModel.set(Duration.fromMillis(safeCount).shiftTo("days", "hours"));
                this.precisionModel.set(this.precision());
                this.thresholdMinModel.set(this.threshold()?.min);
                this.thresholdMaxModel.set(this.threshold()?.max);
            });
        }
    });

    private loadSettings = effect(() => {
        if (this.element()?.data.content.elementType === ElementTypes.GAUGE) {
            const dataset = this.element()?.data.content.datasets[0];
            if (dataset?.id) {
                this.constructorStore.loadDatasetSettings(dataset.id);
            }
        }
    });

    updateElementSettingsAndThreshold = effect(() => {
        const settings = this.settings();
        const threshold = this.threshold();
        if (!settings || !threshold) return;

        untracked(() => {
            let needsUpdate = false;
            const updatedThreshold = {...threshold};

            if (settings.min !== null && settings.min !== undefined && threshold.min > settings.min) {
                updatedThreshold.min = Math.floor(settings.min - 10);
                needsUpdate = true;
            }
            if (settings.max !== null && settings.max !== undefined && threshold.max < settings.max) {
                updatedThreshold.max = Math.floor(settings.max + 10);
                needsUpdate = true;
            }

            if (needsUpdate) {
                this.constructorStore.updateEditingElementMetadata({
                    threshold: updatedThreshold
                });
            }
        });
    });

    removeDatasets() {
        this.constructorStore.removeEditingElementDatasets(this.datasetsToRemove()!.map(d => d.id));
        this.datasetsToRemove.set([]);
    }

    setWidgetName(name: string) {
        this.constructorStore.updateEditingWidgetProps({name});
    }

    setElementTemporalCount(temporalCount?: number) {
        if (temporalCount && temporalCount !== this.temporalCount()) {
            this.constructorStore.updateEditingElementMetadata({temporalCount});
            this.loadDatasets();
        }
    }

    setElementPrecision(precision?: number) {
        if (precision && precision !== this.precision()!) {
            this.constructorStore.updateEditingElementMetadata({precision});
            this.loadDatasets();
        }
    }

    setElementDatasetType(event: { name: string, value: DatasetTypes }) {
        if (event.value === DatasetTypes.CURRENT_VALUE) {
            this.constructorStore.updateEditingElementMetadata({datasetType: event.value, temporalCount: undefined});
        } else {
            this.constructorStore.updateEditingElementMetadata({
                datasetType: event.value,
                temporalCount: this.temporalCount() || 0
            });
        }
        this.loadDatasets();
    }

    setElementThreshold(min?: number, max?: number) {
        const threshold = this.threshold();
        if (min !== undefined && max !== undefined && (min !== threshold?.min || max !== threshold?.max)) {
            this.constructorStore.updateEditingElementMetadata({threshold: {min, max}});
            this.loadDatasets();
        }
    }

    updateTemporalCountModelDays(days: number) {
        this.temporalCountModel.update(prev => Duration.fromDurationLike(prev.set({days})));
    }

    updateTemporalCountModelHours(hours: number) {
        this.temporalCountModel.update(prev => Duration.fromDurationLike(prev.set({hours})));
    }

    private temporalCountChanged = effect(() => {
        const millis = this.temporalCountModel().toMillis();
        const element = this.element();
        if (millis > 0 && element && element.data.metadata.temporalCount !== undefined) {
            this.setElementTemporalCount(millis);
        }
    });

    private precisionChanged = effect(() => {
        this.setElementPrecision(this.precisionModel());
    });

    private elementThresholdChanged = effect(() => {
        this.setElementThreshold(this.thresholdMinModel(), this.thresholdMaxModel());
    });

    private loadDatasets() {
        this.element()?.data.content.datasets.forEach(dataset => {
            this.datasetStore.loadDataset({
                elementId: this.element()!.id,
                datasetId: dataset.id,
                elementType: this.element()!.data.content.elementType,
                datasetType: this.element()!.data.metadata.datasetType,
                temporalCount: this.element()!.data.metadata.temporalCount,
                precision: this.element()!.data.metadata.precision,
            });
        });
    }
}
